import React from 'react';
import { Code, Text, Box, TextField } from '@scale/scaleui-radix';
import { Column } from '@scale/compass-shared/types';
import type { SphereEngineExecutionContext } from '@scale/genai-compass-shared/cards/sphere_engine_execution';
import { SelectMenu } from '@scale/scaleui';

interface FileColumnSelectionProps {
  context: SphereEngineExecutionContext;
  columns: Column[];
  matchedColumns: Column[];
  regexError: string;
  onFileColumnRegexChange: (regex: string) => void;
  onSelectedFileColumnsByIds: (ids: string[]) => void;
}

// Helper component to render regex feedback
const RegexFeedback = ({
  regexError,
  matchedColumns,
  hasRegex,
}: {
  regexError: string;
  matchedColumns: Column[];
  hasRegex: boolean;
}) => {
  if (regexError) {
    return (
      <Text as="p" size="1" className="text-red-700 mt-1">
        Error: {regexError}
      </Text>
    );
  }

  if (matchedColumns.length > 0) {
    return (
      <Text as="p" size="1" className="text-green-700 mt-1">
        Matches {matchedColumns.length} column(s):{' '}
        {matchedColumns.map(col => col.header).join(', ')}
      </Text>
    );
  }

  if (hasRegex) {
    return (
      <Text as="p" size="1" className="text-orange-700 mt-1">
        No columns match this pattern
      </Text>
    );
  }

  return null;
};

const FileColumnSelection: React.FC<FileColumnSelectionProps> = ({
  context,
  columns,
  matchedColumns,
  regexError,
  onFileColumnRegexChange,
  onSelectedFileColumnsByIds,
}) => {
  // Create options for file column selection
  const fileColumnOptions = Array.isArray(columns)
    ? columns
        .filter(col => col && col.header)
        .map(col => ({
          id: col.id,
          name: col.header,
        }))
    : [];

  return (
    <div className="p-4 border border-gray-200 rounded-md bg-gray-50">
      <div className="mb-3">
        <Text as="p" size="2" className="font-medium">
          File Injection Configuration
        </Text>
      </div>

      {/* Regex Input */}
      <div className="mb-4">
        <Text as="p" size="2" className="text-gray-700 mb-2">
          Column Regex Pattern (optional - takes precedence over manual selection)
        </Text>
        <Box>
          <TextField.Root
            placeholder="e.g., .*\.py$ (matches columns ending with .py)"
            value={context.fileColumnRegex || ''}
            onChange={e => onFileColumnRegexChange(e.target.value)}
            size="2"
          />
        </Box>
        {context.fileColumnRegex && (
          <div className="mt-2 p-2 bg-blue-50 rounded">
            <Text as="p" size="1" className="text-blue-700">
              Regex pattern: <Code>{context.fileColumnRegex}</Code>
            </Text>
            <RegexFeedback
              regexError={regexError}
              matchedColumns={matchedColumns}
              hasRegex={!!context.fileColumnRegex}
            />
          </div>
        )}
      </div>

      {/* Manual Column Selection - disabled when regex is provided */}
      <div className="mb-3">
        <Text as="p" size="2" className="text-gray-700 mb-2">
          {context.fileColumnRegex
            ? 'Manual column selection (disabled when regex is provided)'
            : 'Select columns that contain file contents to inject into the Sphere submission. Column names will be used as filenames (e.g., column "foo.py" contains Python code).'}
        </Text>
      </div>
      <Box className="w-full">
        <SelectMenu
          className="w-full"
          multiple={true}
          options={fileColumnOptions}
          selectedOptionId={context.fileColumnIds}
          setSelected={onSelectedFileColumnsByIds}
          disabled={!!context.fileColumnRegex}
          placeholder="Select columns..."
        />
      </Box>
      {Array.isArray(context.fileColumnIds) &&
        context.fileColumnIds.length > 0 &&
        !context.fileColumnRegex && (
          <div className="mt-2 p-2 bg-blue-50 rounded">
            <Text as="p" size="1" className="text-blue-700">
              Selected files: {context.fileColumnIds.join(', ')}
            </Text>
          </div>
        )}
    </div>
  );
};

export default FileColumnSelection;
